import { useEffect, useRef, useState, type FormEvent, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ROUNDING_RULES,
  SERVICE_CHARGE_SCOPES,
  type GeneralSettingsDto,
  type RoundingRule,
  type ServiceChargeScope,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import {
  Field,
  FieldGroup,
  ImageUpload,
  PageHeading,
  SaveBar,
  Section,
  Toggle,
  ghostPillClass,
  inputClass,
} from './controls';
import { useGeneralForm, type GeneralSettingsPatch } from './useGeneralForm';
import { useUnsavedGuard } from './useUnsavedGuard';

/** Millidinars -> whole IQD for the Monthly Target input. */
function mdToIqd(md: string): string {
  // Integer division on the string's BigInt: Number() would lose precision on a
  // venue target in the tens of millions of dinars.
  return (BigInt(md || '0') / 1000n).toString();
}

/** How a control the signed-in employee may read but not change looks. */
const LOCKED_CONTROL = 'disabled:cursor-not-allowed disabled:opacity-50';

/**
 * What the colour pickers show while the venue has no colour of its own —
 * the staff app's built-in accents (styles/theme.css). A colour input cannot
 * show "none", so it shows what the app is actually painting.
 */
const BUILT_IN_PRIMARY = '#22d3ee';
const BUILT_IN_SECONDARY = '#8b5cf6';

/**
 * Paints the saved accents — and the tab title — at once, on THIS device.
 * `VenueTheme` applies both from the sign-in payload, which only changes at the
 * next sign-in or refresh; without this the owner would press Save and see
 * nothing happen. Null puts the built-in colour back, exactly as VenueTheme
 * does. Other devices pick the change up on their next refresh: they read the
 * same two columns.
 */
export function applyVenueBranding(
  primary: string | null,
  secondary: string | null,
  venueName: string,
  root: HTMLElement = document.documentElement,
  doc: Pick<Document, 'title'> = document,
): void {
  if (primary) root.style.setProperty('--brand-primary', primary);
  else root.style.removeProperty('--brand-primary');
  if (secondary) root.style.setProperty('--brand-secondary', secondary);
  else root.style.removeProperty('--brand-secondary');
  if (venueName.trim()) doc.title = venueName.trim();
}

/**
 * The fields THIS page writes, from any copy of the row.
 *
 * One function, two callers: Confirm sends it built from the edited `form`,
 * and the unsaved-changes guard compares that against the same shape built
 * from the row the server last gave us. Built twice by hand, the guard would
 * drift from the payload and start either crying wolf or missing edits.
 *
 * The service charge rides along only for the OWNER, who alone may change it
 * (the server refuses anyone else's change with `owner_only`). A manager's
 * page leaves it out rather than echoing it: the server would accept the echo
 * unchanged, but a copy loaded before the owner's latest edit would echo the
 * OLD rate back — refused, and the manager's whole save with it.
 */
export function venuePayload(
  row: GeneralSettingsDto,
  monthlyTargetIqd: string,
  isOwner: boolean,
): GeneralSettingsPatch {
  return {
    // Venue row fields, moved here with the Public page's removal (2026-09-24).
    venueName: row.venueName,
    primaryColor: row.primaryColor,
    secondaryColor: row.secondaryColor,
    logoMediaAssetId: row.logoMediaAssetId,
    wifiQrMediaAssetId: row.wifiQrMediaAssetId,
    slogan: row.slogan,
    currencySymbol: row.currencySymbol,
    monthlyTarget: Number(monthlyTargetIqd) || 0,
    roundingRule: row.roundingRule,
    checkoutRoundingStepIqd: row.checkoutRoundingStepIqd,
    ...(isOwner
      ? {
          serviceChargePercent: row.serviceChargePercent,
          serviceChargeScope: row.serviceChargeScope,
        }
      : {}),
    enableNegativeSales: row.enableNegativeSales,
    enableBalanceRefund: row.enableBalanceRefund,
    openingTime: row.openingTime,
    closingTime: row.closingTime,
    enableBootCampOffer: row.enableBootCampOffer,
    enableReservationAlerts: row.enableReservationAlerts,
    // Both were read by the Device List, the kiosk and the Dashboard long
    // before anything could set them (2026-09-24).
    aboutToEndThresholdMinutes: row.aboutToEndThresholdMinutes,
    defaultLowStockThreshold: row.defaultLowStockThreshold,
  };
}

/**
 * Venue — the venue's own identity, its money rules and its trading day
 * (SPEC §6.1, first of the four pages the old single page was split into).
 *
 * It PATCHes only the fields below; printing, the portal and loyalty are three
 * other pages writing three other slices of the same row. See `useGeneralForm`
 * for why that is safe.
 */
export function VenuePage() {
  const { t } = useTranslation();
  const { data, form, set, setForm, loading, error, errorFor, saving, saved, save } =
    useGeneralForm();
  // The service charge is the owner's to set (owner, 2026-09-23: "the owner
  // would set it") — not a permission key, so `can()` is the wrong question.
  // The server refuses anyone else's change; this keeps the controls from
  // offering an edit it would refuse.
  const { employee } = useAuth();
  const isOwner = employee?.isOwner === true;

  /*
   * The only field not held in `form`: it is edited in whole IQD while the row
   * carries millidinars, and it is kept as a STRING because the owner types
   * into it — coercing every keystroke to a number makes a half-typed value
   * jump and refuse a backspace. Seeded from the SERVER's copy, so a save's
   * re-read refreshes it and typing does not fight itself.
   */
  const [monthlyTarget, setMonthlyTarget] = useState('0');

  useEffect(() => {
    if (data) setMonthlyTarget(mdToIqd(data.monthlyTargetMd));
  }, [data]);

  /*
   * Dirty means "what Confirm would send differs from what the server holds",
   * never "a field was focused": re-typing the same value, or the load itself,
   * must not raise a prompt. Above the early returns because hooks are.
   */
  useUnsavedGuard(
    !!form &&
      !!data &&
      JSON.stringify(venuePayload(form, monthlyTarget, isOwner)) !==
        JSON.stringify(venuePayload(data, mdToIqd(data.monthlyTargetMd), isOwner)),
  );

  if (loading && !form)
    return <p className="p-6 text-sm text-content-muted">{t('common.loading')}</p>;
  if (error) return <p className="p-6 text-sm text-danger">{error}</p>;
  if (!form) return null;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!form) return;

    if (await save(venuePayload(form, monthlyTarget, isOwner), t('settings.general.saveFailed'))) {
      applyVenueBranding(form.primaryColor, form.secondaryColor, form.venueName);
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="p-6">
      <PageHeading
        title={t('settings.general.title')}
        description={t('settings.general.venueDescription')}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Section id="set-identity" title={t('settings.general.identitySection')}>
            {/* Heads every receipt and report, and the kiosk lock screen. It
                lived on the Public page until that page was removed. */}
            <Field
              label={t('settings.general.venueName')}
              hint={t('settings.general.venueNameHint')}
              error={errorFor('venueName')}
            >
              <input
                value={form.venueName}
                onChange={(event) => set('venueName', event.target.value)}
                required
                maxLength={120}
                className={inputClass}
              />
            </Field>

            <ImageUpload
              label={t('settings.general.logo')}
              kind="LOGO"
              currentUrl={form.logoUrl}
              onUploaded={(assetId, url) =>
                setForm((current) =>
                  current ? { ...current, logoMediaAssetId: assetId, logoUrl: url } : current,
                )
              }
              onCleared={() =>
                setForm((current) =>
                  current ? { ...current, logoMediaAssetId: null, logoUrl: null } : current,
                )
              }
            />

            {/*
              Owner, 2026-09-20: 'on the sign in page i want there to be a QR
              scan for the public wifi which i will upload'. It sits beside the
              logo because it is the same kind of thing — a venue asset the
              kiosk shows.

              An uploaded IMAGE, not an SSID and a password: the code has to
              carry the network exactly as the router publishes it, and one
              built from fields nobody scan-tested would fail silently the day
              the router changed.
            */}
            <FieldGroup
              label={t('settings.general.wifiQr')}
              hint={t('settings.general.wifiQrHint')}
              error={errorFor('wifiQrMediaAssetId')}
            >
              <ImageUpload
                label={t('settings.general.wifiQrUpload')}
                kind="WIFI_QR"
                currentUrl={form.wifiQrUrl}
                // Square and large enough to actually scan off this screen,
                // which is the only way to check the code works before it is
                // in front of customers.
                previewClassName="h-40 w-40"
                onUploaded={(assetId, url) =>
                  setForm((current) =>
                    current ? { ...current, wifiQrMediaAssetId: assetId, wifiQrUrl: url } : current,
                  )
                }
                onCleared={() =>
                  setForm((current) =>
                    current ? { ...current, wifiQrMediaAssetId: null, wifiQrUrl: null } : current,
                  )
                }
              />
            </FieldGroup>

            <Field label={t('settings.general.slogan')}>
              <input
                value={form.slogan ?? ''}
                onChange={(event) => set('slogan', event.target.value || null)}
                className={inputClass}
              />
            </Field>
          </Section>

          {/*
            The staff app's two accent colours — the one thing on the old
            Public page besides the name that anything used. Small on purpose:
            it themes this app, not the kiosk or the receipts.
          */}
          <Section id="set-staff-colours" title={t('settings.general.staffColoursSection')}>
            <p className="text-xs text-content-muted">{t('settings.general.staffColoursHint')}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <StaffColourField
                label={t('settings.general.primaryColour')}
                value={form.primaryColor}
                builtIn={BUILT_IN_PRIMARY}
                error={errorFor('primaryColor')}
                onChange={(colour) => set('primaryColor', colour)}
              />
              <StaffColourField
                label={t('settings.general.secondaryColour')}
                value={form.secondaryColor}
                builtIn={BUILT_IN_SECONDARY}
                error={errorFor('secondaryColor')}
                onChange={(colour) => set('secondaryColor', colour)}
              />
            </div>
          </Section>

          <Section id="set-business-day" title={t('settings.general.businessDaySection')}>
            {/* Opening/Closing Time define the business day and feed every
                report, so both are HH:mm and validated server-side too. */}
            <Field
              label={t('settings.general.openingTime')}
              hint={t('settings.general.businessDayHint')}
              error={errorFor('openingTime')}
            >
              <input
                type="time"
                value={form.openingTime}
                onChange={(event) => set('openingTime', event.target.value)}
                className={`${inputClass} tabular`}
              />
            </Field>

            <Field label={t('settings.general.closingTime')} error={errorFor('closingTime')}>
              <input
                type="time"
                value={form.closingTime}
                onChange={(event) => set('closingTime', event.target.value)}
                className={`${inputClass} tabular`}
              />
            </Field>
          </Section>
        </div>

        <div className="space-y-4">
          <Section id="set-money" title={t('settings.general.moneySection')}>
            <Field label={t('settings.general.currencySymbol')} error={errorFor('currencySymbol')}>
              <input
                value={form.currencySymbol}
                onChange={(event) => set('currencySymbol', event.target.value)}
                className={inputClass}
              />
            </Field>

            <Field
              label={t('settings.general.monthlyTarget')}
              hint={t('settings.general.monthlyTargetHint', { symbol: form.currencySymbol })}
              error={errorFor('monthlyTarget')}
            >
              <input
                type="number"
                min="0"
                step="1"
                value={monthlyTarget}
                onChange={(event) => setMonthlyTarget(event.target.value)}
                className={`${inputClass} tabular`}
              />
            </Field>

            <Field
              label={t('settings.general.roundTheAmount')}
              hint={t('settings.general.roundingHint')}
            >
              <select
                value={form.roundingRule}
                onChange={(event) => set('roundingRule', event.target.value as RoundingRule)}
                className={inputClass}
              >
                {ROUNDING_RULES.map((rule) => (
                  <option key={rule} value={rule}>
                    {t(`settings.general.rounding.${rule}`)}
                  </option>
                ))}
              </select>
            </Field>

            {/* The rule above says which WAY; this says how far. Together they
                are one decision, so they sit together. */}
            <Field
              label={t('settings.general.roundingStep')}
              hint={t('settings.general.roundingStepHint')}
            >
              <input
                type="number"
                min={0}
                max={10000}
                step={50}
                value={form.checkoutRoundingStepIqd}
                onChange={(event) =>
                  set('checkoutRoundingStepIqd', Math.max(0, Number(event.target.value) || 0))
                }
                className={inputClass}
              />
            </Field>

            {/*
              Owner, 2026-09-23: "we should be able to add service charge to
              all the receipts where it would be added before discount ... the
              owner would set it but the cashier could remove the service for
              any customer they want". A whole percent; 0 switches it off and
              every receipt stops mentioning it. It sits with the rounding
              because the two are read together at every checkout.

              Shown to everyone who can open this page — a manager still needs
              to know what the till adds — but editable by the owner alone.
            */}
            {!isOwner ? (
              <p className="text-xs text-content-muted" data-service-charge-owner-only>
                {t('venueServiceCharge.ownerOnly')}
              </p>
            ) : null}
            <Field
              label={t('venueServiceCharge.percent')}
              hint={`${t('venueServiceCharge.percentHint')} ${t('venueServiceCharge.suggestion')}`}
              error={errorFor('serviceChargePercent')}
            >
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                inputMode="numeric"
                data-service-charge-percent
                disabled={!isOwner}
                value={form.serviceChargePercent ?? 0}
                onChange={(event) =>
                  set(
                    'serviceChargePercent',
                    Math.max(0, Math.min(100, Math.floor(Number(event.target.value) || 0))),
                  )
                }
                className={`${inputClass} tabular ${LOCKED_CONTROL}`}
              />
            </Field>

            <Field
              label={t('venueServiceCharge.scope')}
              hint={t('venueServiceCharge.selfServiceNote')}
              error={errorFor('serviceChargeScope')}
            >
              <select
                value={form.serviceChargeScope ?? 'BOTH'}
                data-service-charge-scope
                disabled={!isOwner}
                onChange={(event) =>
                  set('serviceChargeScope', event.target.value as ServiceChargeScope)
                }
                className={`${inputClass} ${LOCKED_CONTROL}`}
              >
                {SERVICE_CHARGE_SCOPES.map((scope) => (
                  <option key={scope} value={scope}>
                    {t(`venueServiceCharge.scopes.${scope}`)}
                  </option>
                ))}
              </select>
            </Field>

            <Toggle
              label={t('settings.general.enableNegativeSales')}
              checked={form.enableNegativeSales}
              onChange={(value) => set('enableNegativeSales', value)}
            />

            <Toggle
              label={t('settings.general.enableBalanceRefund')}
              checked={form.enableBalanceRefund}
              onChange={(value) => set('enableBalanceRefund', value)}
            />
          </Section>

          {/*
            Two numbers every screen already obeyed and nobody could change
            without a database edit (2026-09-24). Plain help text: what each
            one makes happen, and where.
          */}
          <Section id="set-warnings" title={t('settings.general.warningsSection')}>
            <Field
              label={t('settings.general.aboutToEnd')}
              hint={t('settings.general.aboutToEndHint')}
              error={errorFor('aboutToEndThresholdMinutes')}
            >
              <WholeNumberInput
                min={1}
                max={120}
                data-about-to-end
                value={form.aboutToEndThresholdMinutes}
                onValue={(minutes) => set('aboutToEndThresholdMinutes', minutes)}
              />
            </Field>

            <Field
              label={t('settings.general.lowStockDefault')}
              hint={t('settings.general.lowStockDefaultHint')}
              error={errorFor('defaultLowStockThreshold')}
            >
              <WholeNumberInput
                min={0}
                max={100_000}
                data-low-stock-default
                value={form.defaultLowStockThreshold}
                onValue={(level) => set('defaultLowStockThreshold', level)}
              />
            </Field>
          </Section>

          <Section id="set-features" title={t('settings.general.featuresSection')}>
            <Toggle
              label={t('settings.general.enableBootCampOffer')}
              hint={t('settings.general.bootCampHint')}
              checked={form.enableBootCampOffer}
              onChange={(value) => set('enableBootCampOffer', value)}
            />

            <Toggle
              label={t('settings.general.enableReservationAlerts')}
              checked={form.enableReservationAlerts}
              onChange={(value) => set('enableReservationAlerts', value)}
            />
          </Section>
        </div>
      </div>

      <SaveBar saving={saving} saved={saved} error={errorFor('(root)')} />
    </form>
  );
}

/**
 * A number box's text as a whole number within the field's bounds, so the
 * save cannot be refused over a stray decimal or a value past the range the
 * server accepts. Blank reads as the lowest allowed value, which is why
 * {@link WholeNumberInput} never hands it a blank box.
 */
export function wholeWithin(raw: string, min: number, max: number): number {
  const value = Math.floor(Number(raw));
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

/**
 * A whole-number box that shows what the owner types (review find,
 * 2026-09-24). Clamping every keystroke made an emptied box snap back to its
 * minimum: backspacing "10" to type 5 left "1" in the box, the 5 made "15",
 * and Save sent 15 minutes. The text is now the owner's while they type; the
 * form gets each keystroke's bounded number, and the box shows that number
 * when they leave it.
 *
 * A BLANK BOX CHANGES NOTHING: the form keeps the value the box held when the
 * owner went into it — not the last digit they backspaced past — and leaving
 * puts it back. `required` makes the browser refuse an Enter pressed on a
 * blank box instead of saving a number nobody can see.
 */
function WholeNumberInput({
  value,
  min,
  max,
  onValue,
  ...rest
}: {
  value: number;
  min: number;
  max: number;
  onValue: (value: number) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'min' | 'max' | 'onChange'>) {
  const [text, setText] = useState(String(value));
  const entered = useRef(value);

  // A value changed from outside (the re-read after a save) replaces the text;
  // one that only restates what is typed ("500" held as 120) leaves it be.
  useEffect(() => {
    setText((shown) =>
      shown.trim() === '' || wholeWithin(shown, min, max) === value ? shown : String(value),
    );
  }, [value, min, max]);

  return (
    <input
      type="number"
      step={1}
      inputMode="numeric"
      required
      {...rest}
      min={min}
      max={max}
      value={text}
      onFocus={() => {
        entered.current = value;
      }}
      onChange={(event) => {
        const raw = event.target.value;
        setText(raw);
        onValue(raw.trim() === '' ? entered.current : wholeWithin(raw, min, max));
      }}
      onBlur={() => setText(String(value))}
      className={`${inputClass} tabular`}
    />
  );
}

/**
 * One staff-app accent: the picker, and a way back to the built-in colour.
 *
 * A colour input cannot show "none", so while the venue has no colour of its
 * own it shows the built-in one and says so. "Reset to default" is per colour
 * (2026-09-24): an owner who likes the new primary but not the secondary
 * should not have to lose both.
 */
function StaffColourField({
  label,
  value,
  builtIn,
  error,
  onChange,
}: {
  label: string;
  value: string | null;
  builtIn: string;
  error?: string;
  onChange: (colour: string | null) => void;
}) {
  const { t } = useTranslation();
  // A group, not a `<label>`: the reset button is a second control, and a
  // label around two would hand clicks on its caption to the first.
  return (
    <FieldGroup label={label} error={error}>
      <input
        type="color"
        aria-label={label}
        value={value ?? builtIn}
        onChange={(event) => onChange(event.target.value)}
        className={`${inputClass} h-10 p-1`}
      />
      {value === null ? (
        <span className="mt-1 block text-xs text-content-muted" data-built-in-colour>
          {t('settings.general.builtInColour')}
        </span>
      ) : (
        <button
          type="button"
          onClick={() => onChange(null)}
          className={`${ghostPillClass} mt-2`}
          data-reset-colour
        >
          {t('settings.general.resetColour')}
        </button>
      )}
    </FieldGroup>
  );
}
